import React from "react";
import {
  MoreVertical,
  Plus,
  X,
  Zap,
  UserSearch,
  ShoppingCart,
  Ticket,
  Share2,
  UserCog,
  MessageSquareText,
  Mail,
  Radio,
  Phone,
  Sparkles,
  ShieldCheck,
  Wand2,
  Tags,
  FileClock,
  FileText,
  Braces,
  UserPlus2,
  UserCheck,
  Headset,
  UserPlus,
  Octagon,
  ArrowUpRight,
  RotateCcw,
  GitBranch,
  HelpCircle,
  Hourglass,
  MessageCircle,
  BellRing,
  Tag,
  TrendingUp,
  BrainCircuit,
  Clock,
  CalendarClock,
  RotateCw,
  Users,
  Handshake,
  ArrowUpDown,
  Globe,
  PackageCheck,
  Link2,
  FileOutput,
  RefreshCw,
  Play,
  SkipForward,
  Variable,
  ListChecks,
  GalleryHorizontal,
  Loader2,
  CheckCircle2,
  Info,
} from "lucide-react";
import { FlowNode, InsertionTarget, TriggerInstance } from "./types";
import { ON_FAILURE_BRANCH_ID, findActionDef, findTriggerDef } from "./catalog";
import { isGroupedCondition } from "./flow-model";

// Attribute a "+" slot carries its InsertionTarget under (JSON-encoded), so
// the builder page can hit-test `document.elementFromPoint(x, y)` against it
// during a custom pointer-based drag from the left palette — see add-panel.tsx.
export const JOURNEY_DROP_ATTR = "data-journey-drop-target";

// The one fixed drop target for picking a trigger (see TriggerConfig's new
// catalog picker in config-panel.tsx) — unlike JOURNEY_DROP_ATTR, this never
// needs to encode *which* target (there's only ever one trigger box), so its
// value is unused; presence alone marks the element.
export const TRIGGER_DROP_ATTR = "data-trigger-drop-target";

// Small circular icon per node type — everything except the "send" actions
// (which get the bigger boxed "Action" card, matching the reference design).
export const NODE_ICONS: Record<string, React.ComponentType<any>> = {
  fetch_profile: UserSearch,
  fetch_order: ShoppingCart,
  fetch_ticket: Ticket,
  segment: Share2,
  update_profile: UserCog,
  send_message: MessageSquareText,
  send_email: Mail,
  send_whatsapp: MessageSquareText,
  // Real catalog ids (GET /v1/bot/actions) that were missing entirely —
  // fell back to the generic HelpCircle icon and the small circular badge
  // instead of a proper card (see SEND_TYPES/CARD_TYPES below).
  send_menu: ListChecks,
  send_carousel: GalleryHorizontal,
  send_rcs: Radio,
  make_voice_call: Phone,
  ai_generate: Sparkles,
  ai_moderate: ShieldCheck,
  ai_format: Wand2,
  ai_classify: Tags,
  ai_summarize: FileClock,
  assign_people: UserPlus2,
  assign_agent: UserCheck,
  create_crm_task: UserPlus2,
  create_support_ticket: Headset,
  create_lead: UserPlus,
  end_workflow: Octagon,
  escalate: ArrowUpRight,
  fallback: RotateCcw,
  branch: GitBranch,
  condition: HelpCircle,
  wait: Hourglass,
  // Added for the real backend catalog (GET /v1/bot/actions) — ids that
  // weren't in the original hardcoded set.
  send_sms: MessageCircle,
  send_push: BellRing,
  add_tag: Tag,
  remove_tag: Tag,
  score: TrendingUp,
  ai_decide: BrainCircuit,
  wait_until: Clock,
  wait_until_event: Hourglass,
  schedule: CalendarClock,
  retry: RotateCw,
  assign_crm_agent: Users,
  create_deal: Handshake,
  update_lifecycle_stage: ArrowUpDown,
  call_api: Globe,
  confirm_order: PackageCheck,
  escalate_to_human: ArrowUpRight,
  webhook: Link2,
  export: FileOutput,
  crm_sync: RefreshCw,
  start_workflow: Play,
  go_to: SkipForward,
  set_variable: Variable,
};

const SEND_TYPES = new Set(["send_message", "send_email", "send_whatsapp", "send_rcs", "send_sms", "send_push", "make_voice_call", "send_menu", "send_carousel"]);
// Types that render as the bigger boxed card (header + content row) rather
// than the small circular icon+label badge — matches the reference design,
// which also boxes Fetch and Condition steps rather than badging them.
// "wait"/"wait_until_event" stay small badges — confirmed against the real
// design, their config panel is just a duration + continue/end, same as any
// other quick action.
const CARD_TYPES = new Set([
  ...SEND_TYPES,
  "ai_generate",
  "ai_classify",
  "ai_decide",
  "ai_moderate",
  "fetch_profile",
  "condition",
  "assign_people",
  "create_crm_task",
]);

// Per-type verb shown on a node while a Test Run is passing through it (see
// builder-page.tsx's simulation) — falls back to "Running…" for any type not
// listed here.
const RUNNING_LABEL: Record<string, string> = {
  fetch_profile: "Running…",
  fetch_order: "Running…",
  fetch_ticket: "Running…",
  ai_generate: "Generating…",
  ai_moderate: "Generating…",
  ai_format: "Generating…",
  ai_classify: "Generating…",
  ai_summarize: "Generating…",
  condition: "Checking…",
  branch: "Checking…",
  wait: "Waiting…",
  wait_until_event: "Waiting…",
};
export const runningLabelFor = (type: string): string => RUNNING_LABEL[type] || (SEND_TYPES.has(type) ? "Sending…" : "Running…");

// A send node right after an AI Generate step reads its content off that
// step's output ("Generates {} <name>", braces = dynamic value); any other
// send node shows its own named template ("Sends 📄 <name>").
const SEND_SUFFIX: Record<string, string> = {
  send_sms: "as an SMS",
  send_push: "a PUSH notification",
};

const compactTimeout = (label: string) => (/immediately/i.test(label) ? "Immediate {< 1 sec}" : label);

// A Branch node's own `timeout_ms` (confirmed real, e.g. 1000 for
// fraud_anomaly_alert's sub-second fan-out) rendered through the same
// "Immediate {< 1 sec}" phrasing the trigger's own timeout badge uses.
const formatTimeoutMs = (ms?: number): string => {
  if (ms === undefined || ms <= 1000) return "immediately";
  if (ms < 60000) return `${Math.round(ms / 1000)} sec`;
  return `${Math.round(ms / 60000)} min`;
};

const Badge: React.FC<{ tone: "rose" | "violet"; children: React.ReactNode }> = ({ tone, children }) => (
  <span
    className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 ${
      tone === "rose"
        ? "text-rose-600 bg-rose-50 dark:bg-rose-500/15 dark:text-rose-400"
        : "text-violet-600 bg-violet-50 dark:bg-violet-500/15 dark:text-violet-400"
    }`}
  >
    {children}
  </span>
);

const Connector: React.FC<{ height?: number }> = ({ height = 28 }) => (
  <div className="w-px bg-indigo-200 dark:bg-indigo-500/30" style={{ height }} />
);

const EndCap: React.FC = () => (
  <div className="flex flex-col items-center gap-1.5">
    <div className="w-7 h-7 rounded-full bg-gray-900 dark:bg-gray-100 flex items-center justify-center">
      <X size={13} className="text-white dark:text-gray-900" strokeWidth={3} />
    </div>
    <span className="text-[10px] font-bold tracking-wide text-gray-500 dark:text-gray-400">END</span>
  </div>
);

// A "+" slot is both a click target (existing flow) and a drop target — an
// action dragged from the left palette and released here inserts right at
// this position, same as picking it after clicking the slot. `active`
// covers both "this is the click-selected insertion point" and "a palette
// item is currently being press-dragged over this slot" — the builder page
// decides which via hit-testing, this component just renders the state.
// The visible dashed circle is only 28px — much too small a target to
// reliably hit mid-drag — so the padded button around it is the real hit
// area for both click and drop; the circle is purely visual.
const AddSlot: React.FC<{ onClick: () => void; active: boolean; dropTargetKey: string }> = ({ onClick, active, dropTargetKey }) => (
  <button
    type="button"
    onClick={onClick}
    {...{ [JOURNEY_DROP_ATTR]: dropTargetKey }}
    title="Add a step — click, or press and drag one from the left panel"
    className={`p-2.5 rounded-full flex items-center justify-center transition-colors ${active ? "bg-indigo-50 dark:bg-indigo-500/15" : ""}`}
  >
    <span
      className={`w-7 h-7 rounded-full border-2 border-dashed flex items-center justify-center transition-transform ${
        active
          ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 scale-110"
          : "border-gray-300 dark:border-gray-700 text-gray-400 hover:border-indigo-400 hover:text-indigo-500"
      }`}
    >
      <Plus size={14} />
    </span>
  </button>
);

interface CanvasProps {
  trigger: TriggerInstance;
  chainRoot: FlowNode | null;
  selectedId: string | null;
  insertionTarget: InsertionTarget | null;
  dragHoverTarget: InsertionTarget | null;
  // Test Run (see builder-page.tsx): the one node currently "executing" gets
  // a highlighted border + a running badge; nodes flagged invalid (missing
  // required content) get a red border + inline error instead.
  runningNodeId?: string | null;
  completedNodeIds?: Set<string>;
  triggerCompleted?: boolean;
  invalidNodeIds?: Set<string>;
  // Whether a trigger is currently being press-dragged over this canvas's
  // trigger box (see builder-page.tsx's triggerDragActive) — purely visual,
  // same idea as dragHoverTarget but for the one fixed trigger slot.
  triggerDragActive?: boolean;
  onSelectTrigger: () => void;
  onSelectNode: (id: string) => void;
  onPickInsertion: (target: InsertionTarget) => void;
  onDeleteNode: (id: string) => void;
  // Removing the trigger itself — resets the canvas back to the empty/dashed
  // "Add or drag to add a trigger" state (see builder-page.tsx's
  // handleDeleteTrigger). Optional since only the filled trigger box needs
  // it; the empty one has nothing to delete.
  onDeleteTrigger?: () => void;
}

export const FlowCanvas: React.FC<CanvasProps> = ({
  trigger,
  chainRoot,
  selectedId,
  insertionTarget,
  dragHoverTarget,
  runningNodeId,
  completedNodeIds,
  triggerCompleted,
  invalidNodeIds,
  triggerDragActive,
  onSelectTrigger,
  onDeleteTrigger,
  onSelectNode,
  onPickInsertion,
  onDeleteNode,
}) => {
  const matchesTarget = (candidate: InsertionTarget | null, t: InsertionTarget) =>
    !!candidate && candidate.parentId === t.parentId && candidate.branch === t.branch;

  const renderSlot = (target: InsertionTarget) => (
    <AddSlot
      active={matchesTarget(insertionTarget, target) || matchesTarget(dragHoverTarget, target)}
      onClick={() => onPickInsertion(target)}
      dropTargetKey={JSON.stringify(target)}
    />
  );

  const renderNodeBadge = (node: FlowNode) => {
    const Icon = NODE_ICONS[node.type] || HelpCircle;
    const isSelected = selectedId === node.id;
    const isRunning = !!runningNodeId && runningNodeId === node.id;
    const isWait = node.type === "wait" || node.type === "wait_until_event";
    const isBranch = node.type === "branch";
    const subLabel = isWait ? `WAIT for ${node.config?.value ?? "—"} ${node.config?.unit ?? ""}` : isBranch ? "Branch" : node.label;
    return (
      <div className="relative group flex flex-col items-center gap-1.5">
        <button type="button" onClick={() => onSelectNode(node.id)} className="flex flex-col items-center gap-1.5">
          <div
            className={`w-11 h-11 rounded-full bg-white dark:bg-gray-900 border-2 flex items-center justify-center shadow-sm transition-colors ${
              isRunning ? "border-indigo-500" : isSelected ? "border-indigo-600" : "border-gray-200 dark:border-gray-700 group-hover:border-indigo-300"
            }`}
          >
            {isRunning ? <Loader2 size={16} className="animate-spin text-indigo-600" /> : <Icon size={18} className={isSelected ? "text-indigo-600" : "text-gray-500 dark:text-gray-400"} />}
          </div>
          <span className="text-xs font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap bg-white dark:bg-gray-900 px-2 py-0.5 rounded border border-gray-100 dark:border-gray-800 flex items-center gap-1.5">
            {subLabel}
            {isBranch && <Badge tone="violet">{compactTimeout(formatTimeoutMs(node.config?.timeout_ms))}</Badge>}
          </span>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteNode(node.id);
          }}
          title="Delete step"
          className={`absolute -top-1 right-2 w-4 h-4 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-400 hover:text-rose-500 hover:border-rose-300 transition-opacity ${
            isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <X size={9} />
        </button>
      </div>
    );
  };

  // `precedingType` is the type of the node right before this one in the
  // chain (undefined if nothing precedes it) — used only to tell whether a
  // send node's content came off an immediately preceding AI Generate step
  // ("Generates {} <name>") vs its own fixed template ("Sends 📄 <name>").
  const renderActionCard = (node: FlowNode, precedingType?: string) => {
    const isSelected = selectedId === node.id;
    const HeaderIcon = NODE_ICONS[node.type] || HelpCircle;
    const isAiGenerate = node.type === "ai_generate";
    const fromAiGenerate = !isAiGenerate && precedingType === "ai_generate";
    const isRunning = !!runningNodeId && runningNodeId === node.id;
    const isInvalid = !!invalidNodeIds?.has(node.id);

    let contentRow: React.ReactNode = null;
    if (node.type === "fetch_profile") {
      // `fetch` (checked attribute ids) and `fetchLabels` (id -> label) are
      // seeded by config-panel.tsx from the real GET /v1/bot/profile-attributes
      // response — rendered as-is here rather than a hardcoded field list, so
      // this reflects whatever that journey's attributes actually are.
      const c = node.config || {};
      const ids: string[] = c.fetch || [];
      const labels: Record<string, string> = c.fetchLabels || {};
      const fields = ids.map((id) => labels[id] || id);
      contentRow = (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          {fields.length > 0 ? (
            <>
              Fetch Customers <span className="font-semibold text-gray-700 dark:text-gray-200">{fields.join(", ")}</span> & Pass it to next
            </>
          ) : (
            findActionDef(node.type)?.description || "Fetches the customer's profile and passes it to the next step."
          )}
        </div>
      );
    } else if (node.type === "assign_people") {
      // `assignerLabel` is seeded by config-panel.tsx from the real
      // GET /v1/bot/assigners response alongside `assignerId`.
      const c = node.config || {};
      contentRow = c.assignerLabel ? (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          Assign this account to <span className="font-semibold text-gray-700 dark:text-gray-200">{c.assignerLabel}</span>
        </div>
      ) : (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          {findActionDef(node.type)?.description || "Hands this account off to a team or queue."}
        </div>
      );
    } else if (node.type === "create_crm_task") {
      // `queueLabel` is seeded by config-panel.tsx from the real
      // GET /v1/bot/assigners response alongside `queue`.
      const c = node.config || {};
      contentRow = c.queueLabel ? (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          Assign this account to <span className="font-semibold text-gray-700 dark:text-gray-200">{c.queueLabel}</span> as a follow-up task.
        </div>
      ) : (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          {findActionDef(node.type)?.description || "Opens a task for a CRM/RM queue."}
        </div>
      );
    } else if (node.type === "condition") {
      const c = node.config || {};
      if (isInvalid) {
        contentRow = (
          <p className="text-xs text-rose-500 mt-1.5">
            Check if <span className="underline font-semibold">Missing Condition!</span>
          </p>
        );
      } else if (isGroupedCondition(c)) {
        const groupCount = (c.conditionGroups || []).length;
        contentRow = (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            <span className="font-semibold text-gray-700 dark:text-gray-200">
              {groupCount} condition group{groupCount === 1 ? "" : "s"}
            </span>
            {c.endWorkflowOnFailure ? ", ends on failure" : ", continues on failure"}
          </p>
        );
      } else if (c.mode === "boolean") {
        contentRow = (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            Check if <span className="font-semibold text-gray-700 dark:text-gray-200">{c.booleanCheckLabel || c.booleanCheck || "condition"}</span>
            {" is "}
            <span className="font-semibold text-gray-700 dark:text-gray-200">{c.booleanTrueValue || "—"}</span>
            {" ?"}
          </p>
        );
      } else {
        const cond = c.conditions?.[0];
        contentRow = (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            Check if <span className="font-semibold text-gray-700 dark:text-gray-200">{cond?.attribute || "—"}</span>
            {" -> is "}
            <span className="font-semibold text-gray-700 dark:text-gray-200">{cond?.value || "—"}</span>
          </p>
        );
      }
    } else if (isAiGenerate) {
      const name = node.config?.contentName || "generated_content";
      contentRow = (
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-1.5 flex-wrap">
          <span>Generates</span>
          <span className="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded px-1.5 py-0.5 truncate">
            <FileText size={11} className="shrink-0" /> {name}
          </span>
        </div>
      );
    } else if (node.type === "send_menu") {
      // No config-panel form yet (see catalog.ts's getDefaultNodeConfig), so
      // a freshly-dropped node has no chips — fall back to the catalog's own
      // description rather than showing a fabricated "0 options" count.
      const chips = node.config?.chips || [];
      contentRow = (
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-1.5 flex-wrap">
          {chips.length > 0 ? (
            <>
              <span>Sends a menu with</span>
              <span className="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded px-1.5 py-0.5">
                <ListChecks size={11} className="shrink-0" /> {chips.length} option{chips.length === 1 ? "" : "s"}
              </span>
            </>
          ) : (
            <span>{findActionDef(node.type)?.description || "Sends a message with quick-reply chips."}</span>
          )}
        </div>
      );
    } else if (node.type === "send_carousel") {
      const cards = node.config?.cards || [];
      contentRow = (
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-1.5 flex-wrap">
          {cards.length > 0 ? (
            <>
              <span>Sends a carousel of</span>
              <span className="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded px-1.5 py-0.5">
                <GalleryHorizontal size={11} className="shrink-0" /> {cards.length} card{cards.length === 1 ? "" : "s"}
              </span>
            </>
          ) : (
            <span>{findActionDef(node.type)?.description || "Sends a horizontally scrollable set of cards."}</span>
          )}
        </div>
      );
    } else if (node.type === "ai_classify") {
      // One sentence, as in the design (dormancy reactivation) — what's being
      // classified, bolded; the tier rules themselves live in the panel.
      const c = node.config || {};
      const money = /balance/i.test(c.instruction || "") || /balance/i.test(c.classifyAttributeLabel || c.classifyAttribute || "");
      const subject = money ? "customer\u2019s balance" : c.classifyAttributeLabel || c.classifyAttribute;
      contentRow = (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          {subject ? (
            <>
              Classify the <span className="font-semibold text-gray-700 dark:text-gray-200">{subject}</span> into the appropriate {money ? "balance " : ""}tier.
            </>
          ) : (
            findActionDef(node.type)?.description || "Classifies the customer into a tier."
          )}
        </p>
      );
    } else if (node.type === "ai_decide") {
      const c = node.config || {};
      const named = ((c.options as { label: string }[]) || []).filter((o) => o.label);
      const isOffer = /offer/i.test(c.decideAttribute || "") || /offer/i.test(c.instruction || "");
      // A branching Decide (churn risk: Others / Premium) reads better as its
      // own instruction — "Choose the appropriate X based on Y", X and Y bold.
      const instructionParts = node.optionBranches?.length && c.instruction ? /^(.*?appropriate )(.+?)( based on )(.+)$/i.exec(c.instruction) : null;
      contentRow = (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          {instructionParts ? (
            <>
              {instructionParts[1]}
              <span className="font-semibold text-gray-700 dark:text-gray-200">{instructionParts[2]}</span>
              {instructionParts[3]}
              <span className="font-semibold text-gray-700 dark:text-gray-200">{instructionParts[4]}</span>
            </>
          ) : node.optionBranches?.length && c.instruction ? (
            c.instruction
          ) : named.length > 0 ? (
            <>
              Decide whether to recommend a{" "}
              {named.map((o, i) => (
                <React.Fragment key={i}>
                  {i > 0 && (i === named.length - 1 ? " or " : ", ")}
                  <span className="font-semibold text-gray-700 dark:text-gray-200">{o.label.toLowerCase()}</span>
                </React.Fragment>
              ))}
              {isOffer ? " offer" : ""}.
            </>
          ) : (
            findActionDef(node.type)?.description || "Decides which option best fits the customer."
          )}
        </p>
      );
    } else if (node.type === "ai_moderate") {
      contentRow = (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          Check the <span className="font-semibold text-gray-700 dark:text-gray-200">generated message</span> against{" "}
          <span className="font-semibold text-gray-700 dark:text-gray-200">banking</span> and{" "}
          <span className="font-semibold text-gray-700 dark:text-gray-200">compliance policies.</span>
        </p>
      );
    } else if (SEND_TYPES.has(node.type)) {
      if (isInvalid) {
        contentRow = (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            Send a {node.type === "send_sms" ? "SMS" : "WhatsApp"} message <span className="text-rose-500 underline font-semibold">Message Missing!</span>
          </p>
        );
      } else {
        const Icon = fromAiGenerate ? Braces : FileText;
        const value = fromAiGenerate ? node.config?.contentName || "generated_content" : node.config?.template || "Not fetched template_1";
        const suffix = SEND_SUFFIX[node.type];
        contentRow = (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-1.5 flex-wrap">
            <span>{fromAiGenerate ? "Generates" : "Sends"}</span>
            <span className="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded px-1.5 py-0.5 truncate">
              <Icon size={11} className="shrink-0" /> {value}
            </span>
            {suffix && <span>{suffix}</span>}
          </div>
        );
      }
    }

    return (
      <div
        onClick={() => onSelectNode(node.id)}
        className={`w-[280px] bg-white dark:bg-gray-900 rounded-xl border-2 shadow-sm px-4 py-3.5 cursor-pointer transition-colors ${
          isRunning
            ? "border-indigo-500 shadow-indigo-100 dark:shadow-none"
            : isInvalid
              ? "border-rose-400"
              : isSelected
                ? "border-indigo-600"
                : "border-gray-200 dark:border-gray-700 hover:border-indigo-300"
        }`}
      >
        <div className="flex items-center gap-1.5">
          <HeaderIcon size={14} className="text-gray-500 dark:text-gray-400 shrink-0" />
          <p className="text-[13px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-tight truncate flex-1">{node.label}</p>
          {isRunning ? (
            <span className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 shrink-0">
              {runningLabelFor(node.type)} <Loader2 size={11} className="animate-spin" />
            </span>
          ) : (
            completedNodeIds?.has(node.id) && <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteNode(node.id);
            }}
            title="Delete step"
            className="text-gray-300 hover:text-rose-500 shrink-0"
          >
            <MoreVertical size={15} />
          </button>
        </div>
        {contentRow}
      </div>
    );
  };

  // Renders a node plus its continuation (recursive).
  const renderNode = (node: FlowNode, precedingType?: string): React.ReactNode => (
    <div key={node.id} className="flex flex-col items-center">
      {CARD_TYPES.has(node.type) ? renderActionCard(node, precedingType) : renderNodeBadge(node)}
      <Connector />
      {renderContinuation(node)}
    </div>
  );

  const renderContinuation = (node: FlowNode): React.ReactNode => {
    if (node.branches) {
      // Dispatches on `node.branches` being populated at all now, not on
      // node.type — generalized per direct ask so a Send node's new
      // "always"/"fallback" edge pair (pending backend deploy — a send's own
      // successful-delivery continuation vs. a fallback path taken when
      // delivery fails, e.g. salary_day_loan's "Preferred channel?" email/SMS
      // split) renders correctly too, the same way holdout/ai_moderate's own
      // divergent edges needed BRANCHING_TYPES widened earlier rather than
      // silently dropping one side. A fresh node from the palette never gets
      // `.branches` unless its type is in BRANCHING_TYPES (see catalog.ts's
      // makeFlowNode) — this only ever fires for a type that's always been
      // branching, or an imported node whose real edges actually diverge.
      //
      // "branch" fans out in parallel (both paths always run, confirmed
      // against GET /v1/bot/journeys/fraud_anomaly_alert's Send SMS + Send
      // Push firing together) — no pill at all, unlike "condition". A Send
      // node's always/fallback pair gets its own labels (DELIVERED/FALLBACK)
      // instead of the generic TRUE/FALSE used for a real condition, since
      // neither side is actually a boolean check.
      const isParallel = node.type === "branch";
      const isSendFallback = SEND_TYPES.has(node.type);
      return (
        <div className="flex items-start justify-center gap-14 relative w-full pt-1">
          <div className="absolute top-1 h-px bg-indigo-200 dark:bg-indigo-500/30" style={{ left: "calc(25% + 22px)", right: "calc(25% + 22px)" }} />
          {(["true", "false"] as const).map((key) => {
            const branch = node.branches![key];
            const target: InsertionTarget = { parentId: node.id, branch: key };
            const pillLabel = isSendFallback ? (key === "true" ? "DELIVERED" : "FALLBACK") : key.toUpperCase();
            return (
              <div key={key} className="flex flex-col items-center">
                <Connector height={16} />
                {!isParallel && (
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white mb-1 ${
                      key === "true" ? "bg-emerald-500" : "bg-rose-500"
                    }`}
                  >
                    {pillLabel}
                  </span>
                )}
                {!isParallel && <Connector height={16} />}
                {branch.behavior === "end" ? <EndCap /> : branch.next ? renderNode(branch.next) : renderSlot(target)}
              </div>
            );
          })}
        </div>
      );
    }

    if ((node.type === "ai_decide" || node.type === "condition") && node.optionBranches) {
      // Real shape confirmed against GET /v1/bot/journeys/churn_risk_escalation
      // — AI Decide's own named options (e.g. "Self-serve offer"/"RM outreach"),
      // each a real outgoing path, not a fixed True/False pair. A grouped
      // Condition node (Figma redesign, 20 Oct) reuses this exact same
      // optionBranches mechanism — one branch per named condition group, plus
      // one sentinel ON_FAILURE_BRANCH_ID branch for "nothing matched". Pill
      // colors rotate through this palette rather than the fixed emerald/rose
      // used for the old binary Condition/Branch, since there can be more
      // than two; the on-failure branch is always rose regardless of index.
      const options: { id: string; label: string }[] = node.type === "ai_decide" ? node.config?.options || [] : [];
      const groups: { id: string; name: string }[] = node.type === "condition" ? node.config?.conditionGroups || [] : [];
      // AI Decide's second path is rose in the design (Others / Premium);
      // a Condition keeps rose for its on-failure branch only.
      const PILL_COLORS =
        node.type === "ai_decide"
          ? ["bg-emerald-500", "bg-rose-500", "bg-amber-500", "bg-sky-500", "bg-violet-500"]
          : ["bg-emerald-500", "bg-indigo-500", "bg-amber-500", "bg-sky-500", "bg-violet-500"];
      return (
        <div className="flex items-start justify-center gap-14 relative w-full pt-1 flex-wrap">
          {node.optionBranches.length > 1 && (
            <div className="absolute top-1 h-px bg-indigo-200 dark:bg-indigo-500/30" style={{ left: "calc(25% + 22px)", right: "calc(25% + 22px)" }} />
          )}
          {node.optionBranches.map((branch, i) => {
            const isFailure = branch.optionId === ON_FAILURE_BRANCH_ID;
            const label = isFailure
              ? "ON FAILURE"
              : node.type === "ai_decide"
                ? options.find((o) => o.id === branch.optionId)?.label || `Option ${i + 1}`
                : groups.find((g) => g.id === branch.optionId)?.name || `Group ${i + 1}`;
            const pillClass = isFailure ? "bg-rose-500" : PILL_COLORS[i % PILL_COLORS.length];
            const target: InsertionTarget = { parentId: node.id, branch: branch.optionId };
            return (
              <div key={branch.optionId} className="flex flex-col items-center">
                <Connector height={16} />
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white mb-1 ${pillClass}`}>{label}</span>
                <Connector height={16} />
                {branch.behavior === "end" ? <EndCap /> : branch.next ? renderNode(branch.next) : renderSlot(target)}
              </div>
            );
          })}
        </div>
      );
    }

    // Linear continuation.
    if ((node.type === "wait" || node.type === "wait_until_event") && node.config?.thenBehavior === "end") return <EndCap />;
    if (node.next) return renderNode(node.next, node.type);
    return renderSlot({ parentId: node.id });
  };

  return (
    <div className="flex flex-col items-center py-8 px-6 min-w-max mx-auto">
      <span className="text-xs font-semibold text-white bg-gray-900 dark:bg-gray-100 dark:text-gray-900 px-3 py-1 rounded-full mb-2">
        Bot Starting Point
      </span>
      {trigger.id === "" ? (
        // No trigger picked yet (a genuinely-from-scratch journey) — both a
        // click target (same onSelectTrigger as the filled box below, opens
        // TriggerConfig's new catalog picker) and a drop target for a
        // trigger dragged from that same picker (see TRIGGER_DROP_ATTR).
        <button
          type="button"
          onClick={onSelectTrigger}
          {...{ [TRIGGER_DROP_ATTR]: "1" }}
          className={`w-[280px] rounded-xl border-2 border-dashed px-4 py-5 transition-colors ${
            triggerDragActive
              ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-500/15"
              : "border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-indigo-300"
          }`}
        >
          <div className="flex items-center gap-1.5 justify-center mb-2">
            <Zap size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
            <p className="text-[13px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-tight">Trigger</p>
            <Info size={13} className="text-gray-300 dark:text-gray-600 shrink-0" />
          </div>
          <div className="flex flex-col items-center gap-1.5">
            <span
              className={`w-7 h-7 rounded-full border-2 border-dashed flex items-center justify-center transition-transform ${
                triggerDragActive ? "border-indigo-600 text-indigo-600 scale-110" : "border-gray-300 dark:border-gray-700 text-gray-400"
              }`}
            >
              <Plus size={14} />
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400">Add or drag to add a trigger</p>
          </div>
        </button>
      ) : (
        <div
          onClick={onSelectTrigger}
          className={`w-[280px] bg-white dark:bg-gray-900 rounded-xl border-2 shadow-sm px-4 py-3.5 cursor-pointer transition-colors ${
            selectedId === null ? "border-indigo-600" : "border-gray-200 dark:border-gray-700 hover:border-indigo-300"
          }`}
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            <Zap size={14} className="text-gray-500 dark:text-gray-400 shrink-0" />
            <p className="text-[13px] font-bold text-gray-900 dark:text-gray-100 uppercase tracking-tight">{trigger.label}</p>
            {trigger.config?.priority && <Badge tone="rose">{trigger.config.priority} priority</Badge>}
            {trigger.config?.timeout && <Badge tone="violet">{compactTimeout(trigger.config.timeout)}</Badge>}
            <div className="flex-1" />
            {triggerCompleted && <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />}
            {onDeleteTrigger ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteTrigger();
                }}
                title="Remove trigger"
                className="text-gray-300 hover:text-rose-500 shrink-0"
              >
                <MoreVertical size={15} />
              </button>
            ) : (
              <MoreVertical size={15} className="text-gray-300 shrink-0" />
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            {findTriggerDef(trigger.id)?.description || "Triggered when this event happens for a customer"}
          </p>
        </div>
      )}
      {trigger.id !== "" && (
        <>
          <Connector />
          {chainRoot ? renderNode(chainRoot) : renderSlot({ parentId: "trigger" })}
        </>
      )}
    </div>
  );
};

export default FlowCanvas;
